<script setup>
import { computed, ref, onMounted, onBeforeUnmount, watch } from 'vue';
import {
  Chart,
  DoughnutController,
  ArcElement,
  Tooltip,
  Legend,
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import {
  buildDoughnutChartOptions,
  createConnectorLinesPlugin,
  createHtmlLegendPlugin,
  DEFAULT_MIN_LABEL_PERCENTAGE,
} from './doughnutChartConfig';

const props = defineProps({
  collection: {
    type: Object,
    required: true,
  },
  chartOptions: {
    type: Object,
    default: () => ({}),
  },
  height: {
    type: String,
    default: '100%',
  },
  legendMode: {
    type: String,
    default: 'canvas',
  },
  minLabelPercentage: {
    type: Number,
    default: DEFAULT_MIN_LABEL_PERCENTAGE,
  },
});

Chart.register(DoughnutController, ArcElement, Tooltip, Legend);

const chartContainer = ref(null);
const legendContainer = ref(null);
const hasHtmlLegend = computed(() => props.legendMode === 'html-bottom');
let chartInstance = null;

const createChart = () => {
  if (!chartContainer.value) return;

  if (chartInstance) {
    chartInstance.destroy();
  }

  const ctx = chartContainer.value.getContext('2d');
  const options = buildDoughnutChartOptions({
    chartOptions: props.chartOptions,
    minLabelPercentage: props.minLabelPercentage,
    hasHtmlLegend: hasHtmlLegend.value,
  });
  const plugins = [
    ChartDataLabels,
    createConnectorLinesPlugin({
      minPercentage: props.minLabelPercentage,
    }),
  ];

  if (hasHtmlLegend.value) {
    plugins.push(createHtmlLegendPlugin(() => legendContainer.value));
  }

  chartInstance = new Chart(ctx, {
    type: 'doughnut',
    data: props.collection,
    options,
    plugins,
  });
};

watch(
  () => [
    props.collection,
    props.chartOptions,
    props.legendMode,
    props.minLabelPercentage,
  ],
  () => {
    createChart();
  },
  { deep: true }
);

onMounted(() => {
  createChart();
});

onBeforeUnmount(() => {
  if (chartInstance) {
    chartInstance.destroy();
    chartInstance = null;
  }
});
</script>

<template>
  <div
    class="donut-chart-container"
    :class="{ 'donut-chart-container--html-legend': hasHtmlLegend }"
    :style="{ height }"
  >
    <div class="donut-chart-canvas-wrapper" data-testid="donut-chart-canvas">
      <canvas ref="chartContainer" />
    </div>
    <div
      v-if="hasHtmlLegend"
      ref="legendContainer"
      class="donut-chart-legend"
      data-testid="donut-chart-html-legend"
    />
  </div>
</template>

<style scoped>
.donut-chart-container {
  position: relative;
  width: 100%;
}

.donut-chart-container--html-legend {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.donut-chart-canvas-wrapper {
  min-height: 0;
  position: relative;
  width: 100%;
}

.donut-chart-container--html-legend .donut-chart-canvas-wrapper {
  flex: 1 1 auto;
}

.donut-chart-legend {
  flex: 0 0 auto;
  max-height: 8rem;
  overflow-x: hidden;
  overflow-y: auto;
  padding-right: 0.25rem;
}
</style>
